// Based mostly on https://github.com/Uniswap/interface/blob/main/src/theme/index.tsx
import { opacify } from './utils'

export const colors = {
  // base
  white: '#FFFFFF',
  black: '#000000',
  // gray
  gray50: '#F5F6FC',
  gray100: '#E8ECFB',
  gray150: '#D2D9EE',
  gray200: '#B8C0DC',
  gray300: '#98A1C0',
  gray400: '#7780A0',
  gray500: '#5D6785',
  gray600: '#404A67',
  gray700: '#293249',
  gray800: '#131A2A',
  gray900: '#0D111C',
  // pink
  pink200: '#FBA4C0',
  pink300: '#FF6FA3',
  pink400: '#FB118E',
  // red
  red300: '#FD766B',
  red400: '#FA2B39',
  red900: '#240800',
  // yellow
  yellow400: '#A08116',
  yellow500: '#866311',
  yellow600: '#5D4204',
  //gold
  gold200: '#EEB317',
  gold400: '#B17900',
  // green
  green200: '#76D191',
  green300: '#40B66B',
  green400: '#1EC5BB',
  // blue
  blue300: '#869EFF',
  blue400: '#4C82FB',
  blue900: '#040E34',
  blueVibrant: '#587BFF',
  // other
  magentaVibrant: '#FC72FF',
  purple900: '#1C0337',
  // networks color
  networkEthereum: '#627EEA',
  networkOptimism: '#FF0420',
  networkOptimismSoft: 'rgba(255, 4, 32, 0.16)',
  networkPolygon: '#A457FF',
  networkArbitrum: '#28A0F0',
  networkBsc: '#F0B90B',
  networkPolygonSoft: 'rgba(164, 87, 255, 0.16)',
  networkEthereumSoft: 'rgba(98, 126, 234, 0.16)',
}

type Theme = typeof darkTheme

const commonTheme = {
  white: colors.white,
  black: colors.black,

  green: colors.green400,
  red: colors.red300,

  chain_1: colors.networkEthereum,
  chain_3: colors.yellow400,
  chain_4: colors.pink400,
  chain_5: colors.green400,
  chain_10: colors.networkOptimism,
  chain_137: colors.networkPolygon,
  chain_42: colors.networkArbitrum,
  chain_56: colors.networkBsc,
  chain_420: colors.networkOptimism,
  chain_42161: colors.networkArbitrum,
  chain_421613: colors.networkArbitrum,
  chain_80001: colors.networkPolygon,
  chain_137_background: colors.purple900,
  chain_10_background: colors.red900,
  chain_42161_background: colors.blue900,
  chain_56_background: colors.networkBsc,

  brandedGradient: 'linear-gradient(139.57deg, #FF79C9 4.35%, #FFB8E2 96.44%);',
  promotionalGradient: 'radial-gradient(101.8% 4091.31% at 0% 0%, #4673FA 0%, #9646FA 100%);',

  hoverState: opacify(24, '#C2A676'),
  hoverDefault: opacify(8, colors.gray300),
}

export const darkTheme = {
  ...commonTheme,

  background: '#0E1218',
  backgroundBackdrop: '#0E1218',
  backgroundSurface: '#0E1218',
  backgroundModule: '#0E1218',
  backgroundInteractive: '#0E1218',
  backgroundFloating: opacify(12, colors.black),
  backgroundOutline: 'rgba(97, 105, 113, 0.2)',
  backgroundScrim: opacify(72, '#0E1218'),

  textPrimary: colors.white,
  textSecondary: '#616971',
  textTertiary: colors.white,

  accentAction: '#C2A676',
  accentActive: '#C2A676',
  accentSuccess: colors.green200,
  accentWarning: colors.gold200,
  accentFailure: colors.red300,
  accentCritical: colors.red300,

  accentActionSoft: opacify(24, '#C2A676'),
  accentActiveSoft: opacify(24, '#C2A676'),
  accentWarningSoft: opacify(24, colors.gold200),
  accentFailureSoft: opacify(12, colors.red300),

  accentTextDarkPrimary: opacify(80, colors.gray900),

  accentTextLightPrimary: colors.gray50,

  deepShadow: '12px 16px 24px rgba(0, 0, 0, 0.24), 12px 8px 12px rgba(0, 0, 0, 0.24), 4px 4px 8px rgba(0, 0, 0, 0.32);',

  networkDefaultShadow: `0px 40px 120px ${opacify(16, '#C2A676')}`,

  stateOverlayHover: opacify(8, colors.gray300),
  stateOverlayPressed: opacify(24, colors.gray200),

  searchOutline: `rgba(255,255,255,0.07)`,

  // own colors
  gray: '#7B8187',
  gray_100: '#61697110',
  gray_200: '#61697120',
  heading: '#FFFFFF',
  accent: '#C2A676',
  accent_200: '#C2A67620',
  navbarBackground: 'transparent',
  navbarForeground: 'rgba(255,255,255,0.6)',
  navbarActiveLinkBackground: '#61697110',
  navbarActiveLinkForeground: '#fff',
  navbarBtnColor: '#C2A676',
  navbarBtnBorder: '#6D778733',
  bodyBackground: '#0E1218',
  transactionConfirmationModalBgColor: '#1D222B',

  btnPrimaryColor: '#030D1B',
  btnPrimaryBackground: 'linear-gradient(90.52deg, #DDC194 0%, #9A7D49 100%);',
  btnBorderRadius: '16px',

  swapBackground: 'transparent',
  swapPaddingX: '20px',
  swapPaddingY: '20px',
  swapHeaderForeground: '#FFFFFF',
  swapHeaderIconColor: '#BCBAAC',

  swapFormInputBorder: '#6D778733',
  swapFormInputBackground: '#3D404566',
  swapFormInputShadow: '0px 4px 20px rgba(60, 69, 78, 0.3)',
  swapFormInputForeground: '#FFFFFF',
  swapFormSelectBackground: '#00000020',
  swapFormLabelForeground: '#BCBAAC',
  swapFormBalanceForeground: '#616971',

  swapTokensIconBackground: '#1D222B',
  swapTokensIconBorder: '#59626F40',
  swapDetailsIconBackground: '#61697120',

  // ADD LIQUIDITY
  btnBgColorSecond: 'rgba(97, 105, 113, 0.2)',
  addLiquidityFieldBorderColor: 'rgba(109, 119, 135, 0.2)',
  addLiquidityFieldValueColor: '#ffffff',
  addLiquidityFieldColorSecondary: '#616971',
  addLiquidityCurrencyFieldBgColor: 'rgba(31, 36, 46, 0.4)',
  addLiquidityFieldBgColorSecondary: 'rgba(97, 105, 113, 0.08)',
  addLiquidityFieldBgColorThird: 'rgba(97, 105, 113, 0.2)',

  // REMOVE LIQUIDITY
  removeLiquidityInfoBgColor: 'rgba(97, 105, 113, 0.08)',
}

export const lightTheme: Theme = {
  ...commonTheme,

  background: '#F7F8F2',
  backgroundBackdrop: colors.white,
  backgroundSurface: colors.white,
  backgroundModule: colors.gray50,
  backgroundInteractive: colors.gray100,
  backgroundFloating: opacify(8, colors.gray700),
  backgroundOutline: colors.gray150,
  backgroundScrim: opacify(60, colors.gray900),

  textPrimary: '#616971',
  textSecondary: '#616971',
  textTertiary: colors.white,

  accentAction: '#616971',
  accentActive: colors.blue400,
  accentSuccess: colors.green300,
  accentWarning: colors.gold400,
  accentFailure: colors.red400,
  accentCritical: colors.red400,

  accentActionSoft: opacify(12, colors.pink400),
  accentActiveSoft: opacify(24, colors.blue400),
  accentWarningSoft: opacify(24, colors.gold400),
  accentFailureSoft: opacify(12, colors.red400),

  accentTextDarkPrimary: opacify(80, colors.gray900),

  accentTextLightPrimary: colors.gray50,

  deepShadow:
    '8px 12px 20px rgba(51, 53, 72, 0.04), 4px 6px 12px rgba(51, 53, 72, 0.02), 4px 4px 8px rgba(51, 53, 72, 0.04);',

  networkDefaultShadow: `0px 40px 120px ${opacify(12, colors.pink400)}`,

  stateOverlayHover: opacify(8, colors.gray300),
  stateOverlayPressed: opacify(24, colors.gray200),

  searchOutline: opacify(1, colors.black),

  // own colors
  gray: '#7B8187',
  gray_100: '#61697110',
  gray_200: '#61697120',
  heading: '#3C454E',
  accent: '#C2A676',
  accent_200: '#C2A67620',
  navbarBackground: '#F7F8F2',
  navbarForeground: '#616971',
  navbarActiveLinkBackground: '#61697110',
  navbarActiveLinkForeground: '#616971',
  navbarBtnColor: '#C2A676',
  navbarBtnBorder: '#6D778733',
  bodyBackground: '#F7F8F2',
  transactionConfirmationModalBgColor: '#E2E2DB',

  btnPrimaryColor: '#030D1B',
  btnPrimaryBackground: 'linear-gradient(90.52deg, #DDC194 0%, #9A7D49 100%);',
  btnBorderRadius: '16px',

  swapBackground: 'transparent;',
  swapPaddingX: '20px',
  swapPaddingY: '20px',
  swapHeaderForeground: '#3C454E',
  swapHeaderIconColor: '#BCBAAC',

  swapFormInputBorder: '#D7D5C6',
  swapFormInputBackground: '#E2E2DB40',
  swapFormInputShadow: '0px 2px 4px -4px rgba(45, 54, 67, 0.04)',
  swapFormInputForeground: '#3C454E',
  swapFormSelectBackground: '#61697110',
  swapFormLabelForeground: '#BCBAAC',
  swapFormBalanceForeground: '#616971',

  swapTokensIconBackground: '#DBDDDC',
  swapTokensIconBorder: '#D7D5C6',
  swapDetailsIconBackground: '#61697120',

  // ADD LIQUIDITY
  btnBgColorSecond: 'rgba(97, 105, 113, 0.08)',
  addLiquidityCurrencyFieldBgColor: 'rgba(226, 226, 219, 0.4)',
  addLiquidityFieldBorderColor: '#d7d5c6',
  addLiquidityFieldBgColorSecondary: 'rgba(97, 105, 113, 0.08)',
  addLiquidityFieldValueColor: '#3C454E',
  addLiquidityFieldColorSecondary: '#616971',
  addLiquidityFieldBgColorThird: 'rgba(97, 105, 113, 0.2)',

  // REMOVE LIQUIDITY
  removeLiquidityInfoBgColor: 'rgba(97, 105, 113, 0.08)',
}
